/**
 * The small motion rules that the short videos of the kit share (designs `asset-redesign/o`, `q`,
 * `s`, `u`): the easings, the beat grid of a song window in video time and the per-frame signals of
 * the song (the kick and the energy). The renderer (music24-api) and the web preview use the same
 * functions, so a preview frame shows the pose of the file. Pure.
 */

/** The beats and the downbeats of a video, in seconds of the video (0 = the first frame). */
export interface VideoBeatGrid {
  beats: number[];
  downbeats: number[];
}

/** The tempo of the grid of a song with no beat (`beatGrid: fallback`): 120 bpm, 4 beats a bar. */
export const FALLBACK_BEAT_SEC = 0.5;

export function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** The progress (0..1) of the time `t` between `from` and `to`. */
export function progress(t: number, from: number, to: number): number {
  return to <= from ? (t >= to ? 1 : 0) : clamp01((t - from) / (to - from));
}

export function outCubic(x: number): number {
  return 1 - (1 - x) ** 3;
}

export function inCubic(x: number): number {
  return x * x * x;
}

/** The back-out easing of the design (overshoot 1.70158). */
export function outBack(x: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (x - 1) ** 3 + c1 * (x - 1) ** 2;
}

/** A 0.25 s nudge (1 → 1.04 → 1) that starts at `at`: the extra scale at `t`. */
export function nudge(t: number, at: number, amount = 0.04, lengthSec = 0.25): number {
  const x = progress(t, at, at + lengthSec);
  return x <= 0 || x >= 1 ? 0 : Math.sin(Math.PI * x) * amount;
}

/** Rounds to 3 decimals: the values of a timeline stay short. */
export function round3(value: number): number {
  // `+ 0` turns -0 into 0, so a pose at the loop point equals the pose of frame 0.
  return Math.round(value * 1000) / 1000 + 0;
}

/**
 * The beat grid of a video that plays the song from `startSec` for `lengthSec`: the beats and the
 * downbeats of the analysis inside the window, moved to video time. A song with no beat grid (or a
 * window with no beat) gets the 120 bpm grid from 0. Pure.
 */
export function videoBeatGrid(
  analysis: { beats: number[]; downbeats: number[]; beatGrid?: 'detected' | 'fallback' } | null,
  startSec: number,
  lengthSec: number
): VideoBeatGrid {
  const inWindow = (values: number[]): number[] =>
    values
      .map((value) => round3(value - startSec))
      .filter((value) => value >= -0.02 && value < lengthSec)
      .map((value) => Math.max(0, value));
  if (analysis !== null && analysis.beatGrid !== 'fallback') {
    const beats = inWindow(analysis.beats);
    const downbeats = inWindow(analysis.downbeats);
    if (beats.length >= 4 && downbeats.length >= 2) {
      return { beats, downbeats };
    }
  }
  const count = Math.ceil(lengthSec / FALLBACK_BEAT_SEC);
  const beats = Array.from({ length: count }, (_, index) => round3(index * FALLBACK_BEAT_SEC));
  return { beats, downbeats: beats.filter((_, index) => index % 4 === 0) };
}

/** The value of `values` nearest to `target` inside [`min`, `max`], or null. */
export function nearestInRange(values: number[], target: number, min: number, max: number): number | null {
  return values
    .filter((value) => value >= min && value <= max)
    .reduce<
      number | null
    >((best, value) => (best === null || Math.abs(value - target) < Math.abs(best - target) ? value : best), null);
}

/**
 * The kick of each frame (0..1, design o and q): the rise of the loudness envelope over the last
 * 0.2 s, gated by the level of the low bands, so a kick drum lifts it and a vocal peak much less.
 * `energy` and `low` hold one value (0..1) per frame; `low` empty opens the gate (no spectrum). Pure.
 */
export function kickValues(energy: readonly number[], low: readonly number[], fps: number): number[] {
  const back = Math.max(1, Math.round(0.2 * fps));
  return energy.map((value, frame) => {
    let floor = value;
    for (let index = Math.max(0, frame - back); index < frame; index += 1) {
      floor = Math.min(floor, energy[index] ?? floor);
    }
    const gate = low.length === 0 ? 1 : (low[frame] ?? 0);
    return round3(clamp01(((value - floor) / 0.5) * (0.4 + 0.6 * gate)));
  });
}
